<template>
  <DefineTemplate v-slot="{ item, otherProps, isOpen }">
    <UITooltip position="right" :disabled="!isRailStyled">
      <template #activator="{ props: tooltipProps }">
        <v-list-item
          v-bind="{
            ...getListItemProps(item, otherProps, isOpen !== undefined),
            ...tooltipProps,
          }"
        >
          <template v-if="isComponentIcon(item.icon)" #prepend>
            <UIIcon class="v-icon v-icon--size-default text-grey-600">
              <component :is="item.icon" class="app-main-menu-custom-icon" />
            </UIIcon>
          </template>
          <v-list-item-title>
            {{ $t(item.title) }}
          </v-list-item-title>
          <template v-if="isOpen !== undefined" #append>
            <UIExpandIndicator :expanded="isOpen" />
          </template>
        </v-list-item>
      </template>
      {{ $t(item.title) }}
    </UITooltip>
  </DefineTemplate>

  <v-navigation-drawer
    ref="drawerRef"
    v-model="drawerVisible"
    class="app-main-menu"
    :class="{ 'app-main-menu--rail-styled': isRailStyled }"
    floating
    :rail="isRail"
    :width="drawerStore.width"
    :rail-width="46"
    mobile-breakpoint="md"
  >
    <!--
    Causes problems (on windows machines) with scrollbars.
    https://jam.dev/c/94620034-fce0-49b6-9d99-a55aea53942b

    <UIDraggableSize :drawer="drawerElement" />
    -->
    <template v-if="mdAndUp" #prepend>
      <div
        class="app-main-menu__edge app-main-menu__edge--top mt-6 flex items-center gap-2.5 pb-3 pr-2 transition-[padding,box-shadow] duration-200 ease-[cubic-bezier(0.4,0,0.2,1)]"
        :class="[
          isRailStyled ? 'pl-[0.4375rem]' : 'pl-4',
          { 'app-main-menu__edge--shadowed': showTopShadow },
        ]"
      >
        <router-link
          data-test-id="menu-logo"
          class="flex size-8 shrink-0 items-center justify-center"
          to="/"
        >
          <img alt="" :src="logoIcon" class="size-8" />
        </router-link>
        <template v-if="!isRailStyled">
          <div class="flex min-w-0 flex-1 flex-col leading-tight">
            <span class="text-sm font-semibold text-grey-900">KINNOVIS</span>
            <span class="text-xs text-grey-600">Manager</span>
          </div>
          <!--
            Translate-y aligns the toggle with the page header sitting in the
            raised panel to the right (which is offset by panel margin + page
            padding), instead of mirroring the logo. Wrapped because
            AppSidebarToggle has a fragment root, so class inheritance is lost.
          -->
          <div class="translate-y-2">
            <AppSidebarToggle :with-separator="false" />
          </div>
        </template>
      </div>
    </template>

    <v-list v-model:opened="openedGroups" open-strategy="multiple">
      <div ref="topSentinelRef" aria-hidden="true" class="size-px" />
      <template v-for="section in navigationSections" :key="section.id">
        <v-list-subheader
          v-if="!isRailStyled"
          class="text-xs font-medium text-grey-500"
        >
          {{ toSentenceCase($t(section.title)) }}
        </v-list-subheader>

        <template v-for="item in section.items" :key="item.id">
          <!-- Menu group in rail mode: flyout menu so sub-items remain reachable -->
          <v-menu
            v-if="isMenuGroup(item) && isRailStyled"
            location="end top"
            open-on-hover
            :close-on-content-click="true"
            :open-delay="50"
            :close-delay="100"
          >
            <template #activator="{ props: activatorProps }">
              <v-list-item
                v-bind="getListItemProps(item, activatorProps, true)"
                color="primary"
                :prepend-icon="
                  isComponentIcon(item.icon) ? undefined : item.icon
                "
                :active="isGroupActive(item)"
                data-test-id="main-menu-group"
              >
                <template v-if="isComponentIcon(item.icon)" #prepend>
                  <UIIcon class="v-icon v-icon--size-default text-grey-600">
                    <component
                      :is="item.icon"
                      class="app-main-menu-custom-icon"
                    />
                  </UIIcon>
                </template>
                <v-list-item-title>
                  {{ $t(item.title) }}
                </v-list-item-title>
              </v-list-item>
            </template>

            <v-list class="app-main-menu-flyout" density="compact">
              <v-list-subheader class="text-xs font-medium text-grey-500">
                {{ $t(item.title) }}
              </v-list-subheader>
              <v-list-item
                v-for="subItem in item.items"
                :key="subItem.id"
                v-bind="subItem.props"
                :active="route.path.startsWith(subItem.path)"
              >
                <v-list-item-title>
                  {{ $t(subItem.title) }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <!-- Menu group in expanded mode -->
          <v-list-group
            v-else-if="isMenuGroup(item)"
            :value="item.id"
            :prepend-icon="item.icon"
            data-test-id="main-menu-group"
          >
            <template #activator="{ props: activatorProps, isOpen }">
              <ReuseTemplate
                :item="item"
                :other-props="activatorProps"
                :is-open="isOpen"
              />
            </template>
            <template v-for="subItem in item.items" :key="subItem.id">
              <ReuseTemplate :item="subItem" />
            </template>
          </v-list-group>
          <template v-else>
            <!-- Menu item -->
            <ReuseTemplate :item="item" />
          </template>
        </template>
      </template>
      <div ref="bottomSentinelRef" aria-hidden="true" class="size-px" />
    </v-list>

    <template #append>
      <div
        class="app-main-menu__edge app-main-menu__edge--bottom flex items-center gap-1 px-[0.4375rem] py-1 transition-shadow duration-200 ease-[cubic-bezier(0.4,0,0.2,1)]"
        :class="{ 'app-main-menu__edge--shadowed': showBottomShadow }"
      >
        <div :class="isRailStyled ? '' : 'min-w-0 flex-1'">
          <AppUserAvatarMenu data-test-id="menu-avatar" :rail="isRailStyled" />
        </div>
        <UIMenu v-if="!isRailStyled" location="end">
          <template #activator="{ props: activatorProps }">
            <UIIconButton
              v-bind="activatorProps"
              icon="mdi-information-outline"
              size="sm"
              data-test-id="menu-app-info-button"
              variant="black"
              class="text-grey-700"
            />
          </template>

          <div
            class="flex w-56 flex-col gap-1 bg-white p-3 text-xs text-grey-700"
          >
            <div v-if="version" class="font-medium text-grey-900">
              v{{ version }}
            </div>
            <div class="text-grey-500">
              Kinnovis GmbH &copy; {{ currentYear }}
            </div>
          </div>
        </UIMenu>
      </div>
    </template>
  </v-navigation-drawer>
</template>

<script lang="ts" setup>
import logoIcon from '@manager/assets/logos/kinnovis-icon.svg';
import AppSidebarToggle from '@manager/components/layout/AppSidebarToggle.vue';
import AppUserAvatarMenu from '@manager/components/layout/AppUserAvatarMenu.vue';
import { useManagerFeature } from '@manager/composables/use-manager-feature';
import {
  type IMenuGroup,
  type IMenuItem,
  isComponentIcon,
  isMenuGroup,
  useNavigationBuilder,
} from '@manager/composables/use-navigation-builder';
import { useUser } from '@manager/composables/use-user';
import { routeNames } from '@manager/router/route-names';
import { useNavigationDrawerStore } from '@manager/store/navigation-drawer-store';
import { createReusableTemplate, useIntersectionObserver } from '@vueuse/core';
import dayjs from 'dayjs';
import UIExpandIndicator from 'shared/ui/registry/kinnovis/ui-expand-indicator/UIExpandIndicator.vue';
import UIIconButton from 'shared/ui/registry/kinnovis/ui-icon-button/UIIconButton.vue';
import UIIcon from 'shared/ui/registry/kinnovis/ui-icon/UIIcon.vue';
import UIMenu from 'shared/ui/registry/kinnovis/ui-menu/UIMenu.vue';
import UITooltip from 'shared/ui/registry/kinnovis/ui-tooltip/UITooltip.vue';
import { getAppInformation } from 'shared/utils';
import { computed, ref, shallowRef, watch, useTemplateRef } from 'vue';
import { useRoute } from 'vue-router';
import { useDisplay } from 'vuetify';
import MaterialSymbolsConvertToTextOutlineRounded from '~icons/material-symbols/convert-to-text-outline-rounded';
import MaterialSymbolsPriorityOutline from '~icons/material-symbols/priority-outline';

const [DefineTemplate, ReuseTemplate] = createReusableTemplate();

const drawer = defineModel<boolean>('open', { default: true });
const currentYear = dayjs().year();
const { version } = getAppInformation();

function toSentenceCase(value: string) {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

const { mdAndUp } = useDisplay();
const { permissions, user } = useUser();
const route = useRoute();
const { getMenu, getGroup, getItem } = useNavigationBuilder();

const isRail = computed(() => mdAndUp.value && !drawer.value);
const isRailStyled = computed(() => isRail.value);
const drawerVisible = computed({
  get: () => (mdAndUp.value ? true : drawer.value),
  set: (value) => {
    drawer.value = value;
  },
});
const { hiddenFeatures } = useManagerFeature();

const drawerRef = ref();
/*
// TODO enable again when resizable navbar is needed
const drawerElement = computed(() => {
  return drawerRef.value?.$el.nextElementSibling;
});
 */
const drawerStore = useNavigationDrawerStore();

const topSentinelRef = useTemplateRef<HTMLElement>('topSentinelRef');
const bottomSentinelRef = useTemplateRef<HTMLElement>('bottomSentinelRef');
const showTopShadow = ref(false);
const showBottomShadow = ref(false);

useIntersectionObserver(topSentinelRef, ([entry]) => {
  showTopShadow.value = !entry.isIntersecting;
});
useIntersectionObserver(bottomSentinelRef, ([entry]) => {
  showBottomShadow.value = !entry.isIntersecting;
});

const navigationSections = shallowRef<ReturnType<typeof getMenu>>([]);
const openedGroups = ref<string[]>([]);

function getListItemProps(
  item: IMenuItem | IMenuGroup,
  otherProps?: Record<string, unknown>,
  isGroupActivator = false
) {
  if (isMenuGroup(item)) {
    return { ...otherProps, appendIcon: undefined };
  }

  return {
    ...otherProps,
    ...item.props,
    ...(isGroupActivator && { appendIcon: undefined }),
    active: route.path.startsWith(item.path),
  };
}

function isGroupActive(group: IMenuGroup) {
  return group.items.some((subItem) => route.path.startsWith(subItem.path));
}

function getActiveGroupId() {
  for (const section of navigationSections.value) {
    for (const item of section.items) {
      if (isMenuGroup(item) && isGroupActive(item)) {
        return item.id;
      }
    }
  }

  return null;
}

function ensureActiveGroupOpened() {
  const activeGroupId = getActiveGroupId();

  if (activeGroupId && !openedGroups.value.includes(activeGroupId)) {
    openedGroups.value = [...openedGroups.value, activeGroupId];
  }
}

watch(
  [permissions, hiddenFeatures, user],
  () => {
    navigationSections.value = getMenu([
      {
        title: 'navigationDrawer.superAdmin',
        items: [
          getItem(
            routeNames.tenantsOverview,
            'mdi-account-box-multiple-outline'
          ),
          getItem(routeNames.maintenance, 'mdi-wrench-cog'),
        ],
      },
      {
        title: 'navigationDrawer.dashboard',
        items: [
          getItem(routeNames.tasksOverview, MaterialSymbolsPriorityOutline),
          getItem(routeNames.facilityMapOverview, 'mdi-layers-outline'),
          getItem(
            routeNames.dashboardOverview,
            'mdi-chart-timeline-variant-shimmer'
          ),
        ],
      },
      {
        title: 'navigationDrawer.salesManagement',
        items: [
          getItem(routeNames.bookingOverview, 'mdi-calendar-refresh'),
          getItem(routeNames.customerOverview, 'mdi-account-multiple-outline'),
          getItem(routeNames.invoiceOverview, 'mdi-file-document-edit-outline'),
          getItem(
            routeNames.creditNoteOverview,
            MaterialSymbolsConvertToTextOutlineRounded
          ),
          getItem(routeNames.unitOverview, 'mdi-lock-open-outline'),
        ],
      },
      {
        title: 'navigationDrawer.locationManagement',
        items: [
          getItem(routeNames.locationOverview, 'mdi-map-marker-outline'),
          getItem(routeNames.unitTypeOverview, 'mdi-cube-outline'),
          getItem(routeNames.insuranceOverview, 'mdi-shield-lock-outline'),
          getItem(
            routeNames.depositOverview,
            'mdi-credit-card-refresh-outline'
          ),
          getItem(routeNames.productOverview, 'mdi-cart-outline'),
          getItem(routeNames.discountOverview, 'mdi-tag-outline'),
          getGroup({
            items: [
              getItem(routeNames.emailOverview, 'mdi mdi-email-fast-outline'),
              getItem(
                routeNames.emailTemplateOverview,
                'mdi-email-edit-outline'
              ),
              getItem(routeNames.emailDomainOverview, 'mdi-at'),
            ],
            icon: 'mdi-email-multiple-outline',
            title: 'default.menu.emails',
          }),
          {
            ...getItem(
              routeNames.documentTemplateList,
              'mdi-file-document-edit-outline'
            ),
            title: 'default.menu.documentTemplates',
          },
        ],
      },
      {
        title: 'navigationDrawer.admin',
        items: [
          getItem(routeNames.connectedApps, 'mdi-connection'),
          getItem(routeNames.userOverview, 'mdi-account-group-outline'),
          getGroup({
            title: 'default.menu.storefront',
            icon: 'mdi-storefront-outline',
            items: [
              getItem(routeNames.globalSettings, 'mdi-web'),
              getItem(
                routeNames.locationSettingsOverview,
                'mdi-map-marker-outline'
              ),
            ],
          }),
          getItem(routeNames.publicApi, 'mdi-api'),
        ],
      },
      {
        title: 'navigationDrawer.feedback',
        items: [getItem(routeNames.votingPortal, 'mdi-thumb-up-outline')],
      },
    ]);

    ensureActiveGroupOpened();
  },
  { immediate: true }
);

watch(
  () => route.path,
  () => {
    ensureActiveGroupOpened();
  },
  { immediate: true }
);
</script>

<style lang="scss">
@property --nav-fade-top {
  syntax: '<length>';
  inherits: false;
  initial-value: 0px;
}

@property --nav-fade-bottom {
  syntax: '<length>';
  inherits: false;
  initial-value: 0px;
}

@keyframes app-main-menu-fade-top {
  from {
    --nav-fade-top: 0px;
  }
  to {
    --nav-fade-top: 2.5rem;
  }
}

@keyframes app-main-menu-fade-bottom {
  from {
    --nav-fade-bottom: 2.5rem;
  }
  to {
    --nav-fade-bottom: 0px;
  }
}

.app-main-menu--rail-styled ~ .v-main .raised-panel {
  margin-left: 0;
}

.app-main-menu.app-main-menu--rail-styled {
  .v-navigation-drawer__content {
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .v-list-item__content,
  .v-list-item__append {
    display: none;
  }

  .v-list-item--density-default.v-list-item--one-line {
    width: 2rem;
    min-height: 2rem;
    padding: 0;
    margin-inline: 0.4375rem 0;
    margin-block: 0;
    grid-template-columns: 1fr;
    grid-template-areas: 'prepend';
    justify-content: stretch;
  }

  .v-list-item__prepend {
    width: 100%;
    margin-inline: 0;
    justify-content: center;
  }

  .v-list-item__prepend > .v-icon ~ .v-list-item__spacer,
  .v-list-item__prepend > .v-badge ~ .v-list-item__spacer,
  .v-list-item__prepend > .v-tooltip ~ .v-list-item__spacer {
    width: 0;
    display: none;
  }

  .v-list-group__items {
    display: none;
  }
}

.app-main-menu {
  @supports (animation-timeline: scroll()) {
    .v-navigation-drawer__content {
      mask-image: linear-gradient(
        to bottom,
        transparent 0,
        black var(--nav-fade-top),
        black calc(100% - var(--nav-fade-bottom)),
        transparent 100%
      );
      // Use longhand properties — esbuild's CSS minifier expands the
      // `animation` shorthand and injects `animation-duration: 0s`, which
      // makes scroll-driven animations complete instantly and pin to their
      // `to` state. With `auto`, the duration tracks the scroll timeline.
      animation-name: app-main-menu-fade-top, app-main-menu-fade-bottom;
      animation-duration: auto, auto;
      animation-timing-function: linear, linear;
      animation-fill-mode: both, both;
      animation-timeline: scroll(self block);
      animation-range:
        0 2.5rem,
        calc(100% - 2.5rem) 100%;
    }
  }

  .app-main-menu__edge {
    position: relative;
    z-index: 1;
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0);
  }

  .app-main-menu__edge--top.app-main-menu__edge--shadowed {
    box-shadow:
      0 1px 0 0 rgba(0, 0, 0, 0.1),
      0 6px 8px -2px rgba(0, 0, 0, 0.18);
  }

  .app-main-menu__edge--bottom.app-main-menu__edge--shadowed {
    box-shadow:
      0 -1px 0 0 rgba(0, 0, 0, 0.1),
      0 -6px 8px -2px rgba(0, 0, 0, 0.18);
  }

  .v-list-item__prepend > .v-badge ~ .v-list-item__spacer,
  .v-list-item__prepend > .v-icon ~ .v-list-item__spacer,
  .v-list-item__prepend > .v-tooltip ~ .v-list-item__spacer {
    width: 0.5rem;
  }

  .v-list-group__items {
    --indent-padding: 1.5rem;
    display: flow-root;
  }

  .v-list {
    padding-top: 0;
  }

  .v-list-subheader {
    min-height: 1.75rem;
    padding-inline: 1rem !important;
  }

  .v-list-subheader ~ .v-list-subheader {
    margin-top: 1rem;
  }

  .v-list-item--density-default.v-list-item--one-line {
    min-height: 1.75rem;
    padding-block: 0.125rem;
    padding-inline-start: calc(0.5rem + var(--indent-padding, 0px));
    padding-inline-end: 0.5rem;
    margin-inline: 0.5rem;
    margin-block: 0.125rem;
    border-radius: 0.375rem;
    font-size: 0.875rem;
  }

  .v-list-group
    > .v-list-item.v-list-item--density-default.v-list-item--one-line {
    margin-bottom: 0;
  }

  .v-list-item__prepend .v-icon {
    font-size: 1rem;
    opacity: 1;
    @apply text-grey-700;
  }

  .v-list-item--active .v-list-item__prepend .v-icon {
    @apply text-app-primary;
  }

  .v-list-item-title {
    font-size: 0.875rem;
    line-height: 1.25rem;
  }

  .v-list-item__overlay,
  .v-ripple__container {
    border-radius: inherit;
  }

  .v-list-item__content {
    hyphens: auto;
    overflow-wrap: normal;
    overflow: hidden;
    padding: 0;
    white-space: nowrap;
    text-overflow: ellipsis;
    word-break: normal;
    word-wrap: break-word;
  }

  // Cross-fade text/append in sync with the drawer's width animation so the
  // open/rail switch looks continuous instead of snapping at the end.
  .v-list-item-title,
  .v-list-item__append {
    transition: opacity 0.15s cubic-bezier(0.4, 0, 0.2, 1);
  }

  &.v-navigation-drawer--rail {
    .v-list-item-title,
    .v-list-item__append {
      opacity: 0;
    }
  }
}

.v-navigation-drawer {
  @apply border-r border-app-separator bg-app-sidebar #{!important};
}

.app-main-menu-flyout {
  min-width: 12rem;
  padding: 0.25rem;
  border-radius: 0.5rem;
  background-color: theme('colors.white');
  border: 1px solid theme('colors.grey.200');
  box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.08);

  .v-list-subheader {
    min-height: 1.75rem;
    padding-inline: 0.5rem !important;
  }

  .v-list-item--density-default.v-list-item--one-line {
    min-height: 2rem;
    padding-inline: 0.5rem;
    margin-block: 0.125rem;
    border-radius: 0.375rem;
    font-size: 0.875rem;
  }

  .v-list-item__prepend .v-icon {
    font-size: 1rem;
    opacity: 1;
    @apply text-grey-700;
  }

  .v-list-item__prepend > .v-icon ~ .v-list-item__spacer,
  .v-list-item__prepend > .v-badge ~ .v-list-item__spacer,
  .v-list-item__prepend > .v-tooltip ~ .v-list-item__spacer {
    width: 0.5rem;
  }

  .v-list-item--active .v-list-item__prepend .v-icon {
    @apply text-app-primary;
  }

  .v-list-item-title {
    font-size: 0.875rem;
    line-height: 1.25rem;
  }
}
</style>
